import React from "react";

import type { IPasswordAuthenticationRequest } from "@mne-authentication/api";
import { Form } from "react-final-form";

import { styled } from "@mui/material/styles";

import { FormattedMessage } from "@mnemonic/intl";
import * as ArgusUtils from "@mnemonic/pure-utils/ArgusUtils";
import { extractErrorMessage } from "@mnemonic/redux-utils";

import {
  LoginBox,
  LoginBoxCallout,
  LoginBoxContent,
  LoginBoxHeader,
  LoginBoxSubHeader,
} from "../../Components/Login/Layout";
import { LoginButtonWithDisclaimer } from "../../Components/LoginButtonWithDisclaimer";
import { PasswordField } from "../../Components/PasswordTextField";
import { UsernameTextField } from "../../Components/UsernameTextField";
import { useAuthenticatePasswordMutation } from "../../Redux/Service/authenticateApi";
import { useTypedDispatch, useTypedSelector } from "../../Redux/createStore";
import { globalSlice } from "../../Redux/globalSlice";
import RedirectWrapper from "./RedirectWrapper";
import { saveLoginDetails } from "./utils/saveLoginDetails";

interface IFormProps extends Omit<IPasswordAuthenticationRequest, "requestedAuthorizations"> {}

const StyledDiv = styled("div")(({ theme }) => ({ paddingBottom: theme.spacing(2) }));

export const LoginPassword = () => {
  const { domain } = ArgusUtils.getTargetMeta();
  const [authenticatePassword, { isLoading, error }] = useAuthenticatePasswordMutation();
  const { lastUsername } = useTypedSelector((state) => state.global);
  const dispatch = useTypedDispatch();
  const errorMessage = extractErrorMessage(error);

  return (
    <RedirectWrapper>
      <LoginBox loading={isLoading}>
        <LoginBoxHeader />
        <LoginBoxContent>
          {errorMessage && <LoginBoxCallout type="error">{errorMessage}</LoginBoxCallout>}
          <LoginBoxSubHeader method={<FormattedMessage id="Password" />} />
          <Form
            initialValues={{ password: null, userName: lastUsername } as IFormProps}
            onSubmit={(formValues: IFormProps, formApi) =>
              authenticatePassword({
                criteria: { userName: formValues.userName, password: formValues.password, domain },
              })
                .unwrap()
                ?.then((payload) => {
                  const isSuccess = saveLoginDetails({ username: formValues.userName, method: "password", payload });
                  dispatch(globalSlice.actions.setLoginSuccess(isSuccess));
                })
                // on error reset password field
                ?.catch((error) => {
                  formApi.change("password", "");
                  console.error(error);
                })
            }
            render={({ values, handleSubmit }) => {
              return (
                <form onSubmit={handleSubmit}>
                  <StyledDiv>
                    <UsernameTextField name="userName" autoFocus={!values.userName} />
                  </StyledDiv>
                  <StyledDiv>
                    <PasswordField name="password" method="static" autoFocus={Boolean(values.userName)} />
                  </StyledDiv>
                  <LoginButtonWithDisclaimer disableButton={isLoading} />
                </form>
              );
            }}
          />
        </LoginBoxContent>
      </LoginBox>
    </RedirectWrapper>
  );
};
